import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from './client'

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const useMe = () =>
  useQuery({
    queryKey: ['me'],
    queryFn:  () => api.get('/auth/me').then(r => r.data),
  })

export const useUpdateMe = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: data => api.patch('/auth/me', data).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['me'] }),
  })
}

// ─── Vehicles ─────────────────────────────────────────────────────────────────

export const useVehicles = () =>
  useQuery({
    queryKey: ['vehicles'],
    queryFn:  () => api.get('/vehicles').then(r => r.data),
  })

export const useVehicle = (id) =>
  useQuery({
    queryKey: ['vehicle', id],
    queryFn:  () => api.get(`/vehicles/${id}`).then(r => r.data),
    enabled:  !!id,
  })

export const useAddVehicle = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: data => api.post('/vehicles', data).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['vehicles'] }),
  })
}

export const useUpdateVehicle = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }) => api.patch(`/vehicles/${id}`, data).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['vehicles'] })
      qc.invalidateQueries({ queryKey: ['vehicle', data.id] })
    },
  })
}

export const useDeleteVehicle = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.delete(`/vehicles/${id}`).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['vehicles'] }),
  })
}

export const useUpdateMileage = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, mileage }) => api.post(`/vehicles/${id}/mileage`, { mileage }).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['vehicles'] })
      qc.invalidateQueries({ queryKey: ['vehicle', data.id] })
    },
  })
}

export const useAddVehicleOwner = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ vehicleId, email }) =>
      api.post(`/vehicles/${vehicleId}/owners`, { email }).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['vehicle', vars.vehicleId] }),
  })
}

export const useRemoveVehicleOwner = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ vehicleId, userId }) =>
      api.delete(`/vehicles/${vehicleId}/owners/${userId}`).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['vehicle', vars.vehicleId] }),
  })
}

// ─── Customers ────────────────────────────────────────────────────────────────

export const useCustomers = () =>
  useQuery({
    queryKey: ['customers'],
    queryFn:  () => api.get('/customers').then(r => r.data),
  })

export const useCustomer = (id) =>
  useQuery({
    queryKey: ['customer', id],
    queryFn:  () => api.get(`/customers/${id}`).then(r => r.data),
    enabled:  !!id,
  })

export const useAddCustomer = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: data => api.post('/customers', data).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['customers'] }),
  })
}

export const useUpdateCustomer = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }) => api.patch(`/customers/${id}`, data).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['customers'] })
      qc.invalidateQueries({ queryKey: ['customer', data.id] })
    },
  })
}

export const useLinkCustomerUser = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ customerId, email }) =>
      api.post(`/customers/${customerId}/link-user`, { email }).then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['customers'] })
      qc.invalidateQueries({ queryKey: ['customer', vars.customerId] })
    },
  })
}

// ─── Service Items ────────────────────────────────────────────────────────────

export const useServiceItems = (garageId) =>
  useQuery({
    queryKey: ['serviceItems', garageId ?? null],
    queryFn:  () => api.get('/service-items', { params: garageId ? { garageId } : {} }).then(r => r.data),
  })

export const useAddServiceItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: data => api.post('/service-items', data).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['serviceItems'] }),
  })
}

export const useDeleteServiceItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: id => api.delete(`/service-items/${id}`).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['serviceItems'] }),
  })
}

// ─── Service Schedules ────────────────────────────────────────────────────────

export const useAllSchedules = () =>
  useQuery({
    queryKey: ['schedules', 'all'],
    queryFn:  () => api.get('/schedules').then(r => r.data),
  })

export const useSchedules = (vehicleId) =>
  useQuery({
    queryKey: ['schedules', vehicleId],
    queryFn:  () => api.get(`/vehicles/${vehicleId}/schedules`).then(r => r.data),
    enabled:  !!vehicleId,
  })

export const useAddSchedule = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ vehicleId, ...data }) =>
      api.post(`/vehicles/${vehicleId}/schedules`, data).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['schedules', vars.vehicleId] }),
  })
}

export const useUpdateSchedule = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ vehicleId, id, ...data }) =>
      api.patch(`/vehicles/${vehicleId}/schedules/${id}`, data).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['schedules', vars.vehicleId] }),
  })
}

export const useDeleteSchedule = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ vehicleId, id }) =>
      api.delete(`/vehicles/${vehicleId}/schedules/${id}`).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['schedules', vars.vehicleId] }),
  })
}

export const useLogService = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ vehicleId, scheduleId, ...data }) =>
      api.post(`/vehicles/${vehicleId}/schedules/${scheduleId}/log`, data).then(r => r.data),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['schedules', vars.vehicleId] })
      qc.invalidateQueries({ queryKey: ['serviceRecords', vars.vehicleId] })
      qc.invalidateQueries({ queryKey: ['vehicle', vars.vehicleId] })
      qc.invalidateQueries({ queryKey: ['vehicles'] })
    },
  })
}

// ─── Service Records ──────────────────────────────────────────────────────────

export const useServiceRecords = (vehicleId) =>
  useQuery({
    queryKey: ['serviceRecords', vehicleId],
    queryFn:  () => api.get(`/vehicles/${vehicleId}/records`).then(r => r.data),
    enabled:  !!vehicleId,
  })

export const useAddServiceRecord = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ vehicleId, ...data }) =>
      api.post(`/vehicles/${vehicleId}/records`, data).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['serviceRecords', vars.vehicleId] }),
  })
}

// ─── Reminders ────────────────────────────────────────────────────────────────

export const useReminders = () =>
  useQuery({
    queryKey: ['reminders'],
    queryFn:  () => api.get('/reminders').then(r => r.data),
  })

export const useAddReminder = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: data => api.post('/reminders', data).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['reminders'] }),
  })
}

export const useDeleteReminder = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: id => api.delete(`/reminders/${id}`).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['reminders'] }),
  })
}

// ─── Issues ───────────────────────────────────────────────────────────────────

export const useIssues = (filters = {}) =>
  useQuery({
    queryKey: ['issues', filters],
    queryFn:  () => api.get('/issues', { params: filters }).then(r => r.data),
  })

export const useIssue = (id) =>
  useQuery({
    queryKey: ['issue', id],
    queryFn:  () => api.get(`/issues/${id}`).then(r => r.data),
    enabled:  !!id,
  })

export const useIssueUpdates = (issueId) =>
  useQuery({
    queryKey: ['issueUpdates', issueId],
    queryFn:  () => api.get(`/issues/${issueId}/updates`).then(r => r.data),
    enabled:  !!issueId,
    refetchInterval: 30000,
  })

export const useAddIssue = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: data => api.post('/issues', data).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['issues'] })
      if (data.vehicle_id) {
        qc.invalidateQueries({ queryKey: ['issues', { vehicleId: data.vehicle_id }] })
      }
    },
  })
}

export const useUpdateIssue = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }) => api.patch(`/issues/${id}`, data).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['issues'] })
      qc.invalidateQueries({ queryKey: ['issue', data.id] })
    },
  })
}

export const useDeleteIssue = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: id => api.delete(`/issues/${id}`).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['issues'] }),
  })
}

export const useRaiseJobRequest = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: data => api.post('/jobs', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  })
}

export const useAcceptJobRequest = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (jobId) => api.post(`/jobs/${jobId}/accept`).then(r => r.data),
    onSuccess: (_, jobId) => {
      qc.invalidateQueries({ queryKey: ['jobs'] })
      qc.invalidateQueries({ queryKey: ['job', jobId] })
      qc.invalidateQueries({ queryKey: ['issues'] })
    },
  })
}

export const useDeclineJobRequest = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (jobId) => api.post(`/jobs/${jobId}/decline`).then(r => r.data),
    onSuccess: (_, jobId) => {
      qc.invalidateQueries({ queryKey: ['jobs'] })
      qc.invalidateQueries({ queryKey: ['job', jobId] })
    },
  })
}

export const useGenerateQuoteFromJob = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (jobId) => api.post(`/jobs/${jobId}/generate-quote`).then(r => r.data),
    onSuccess: (_, jobId) => {
      qc.invalidateQueries({ queryKey: ['jobs'] })
      qc.invalidateQueries({ queryKey: ['job', jobId] })
      qc.invalidateQueries({ queryKey: ['quotes'] })
    },
  })
}

export const useJobMessages = (jobId) =>
  useQuery({
    queryKey: ['jobMessages', jobId],
    queryFn:  () => api.get(`/jobs/${jobId}/messages`).then(r => r.data),
    enabled:  !!jobId,
    refetchInterval: 15000,
  })

export const useAddJobMessage = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ jobId, message }) => api.post(`/jobs/${jobId}/messages`, { message }).then(r => r.data),
    onSuccess: (_, { jobId }) => qc.invalidateQueries({ queryKey: ['jobMessages', jobId] }),
  })
}

export const useCreateJobFromIssue = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (issueId) => api.post(`/issues/${issueId}/create-job`).then(r => r.data),
    onSuccess: (job, issueId) => {
      qc.invalidateQueries({ queryKey: ['issues'] })
      qc.invalidateQueries({ queryKey: ['issue', issueId] })
      qc.invalidateQueries({ queryKey: ['jobs'] })
    },
  })
}

export const useAddIssuePhoto = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ issueId, file, notes }) => {
      const fd = new FormData()
      fd.append('file', file)
      if (notes) fd.append('notes', notes)
      return api.post(`/issues/${issueId}/photos`, fd).then(r => r.data)
    },
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['issue', vars.issueId] }),
  })
}

export const useDeleteIssuePhoto = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ issueId, photoId }) =>
      api.delete(`/issues/${issueId}/photos/${photoId}`).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['issue', vars.issueId] }),
  })
}

// ─── MOT checklist ────────────────────────────────────────────────────────────

export const useMotChecklist = (jobId) =>
  useQuery({
    queryKey: ['motChecklist', jobId],
    queryFn:  () => api.get(`/mot/checklist/${jobId}`).then(r => r.data),
    enabled:  !!jobId,
  })

export const useUpdateMotChecklistItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ jobId, itemId, result, notes }) =>
      api.patch(`/mot/checklist/${jobId}/${itemId}`, { result, notes }).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['motChecklist', vars.jobId] }),
  })
}

// ─── Documents ────────────────────────────────────────────────────────────────

export const useDocuments = (vehicleId) =>
  useQuery({
    queryKey: ['documents', vehicleId],
    queryFn:  () => api.get('/documents', { params: { vehicleId } }).then(r => r.data),
    enabled:  !!vehicleId,
  })

export const useAddDocument = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ vehicleId, file, url, documentType, filename, notes, issueId }) => {
      if (file) {
        const fd = new FormData()
        fd.append('document', file)
        fd.append('vehicleId', vehicleId)
        fd.append('documentType', documentType || 'other')
        if (filename) fd.append('filename', filename)
        if (notes)    fd.append('notes', notes)
        if (issueId)  fd.append('issueId', issueId)
        return api.post('/documents', fd).then(r => r.data)
      }
      return api.post('/documents', { vehicleId, url, documentType, filename, notes, issueId }).then(r => r.data)
    },
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['documents', vars.vehicleId] }),
  })
}

export const useDeleteDocument = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: id => api.delete(`/documents/${id}`).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['documents'] }),
  })
}

// ─── Jobs ─────────────────────────────────────────────────────────────────────

export const useJobs = (filters = {}) =>
  useQuery({
    queryKey: ['jobs', filters],
    queryFn:  () => api.get('/jobs', { params: filters }).then(r => r.data),
  })

export const useJobPartsAlerts = () =>
  useQuery({
    queryKey: ['jobs', 'parts-alerts'],
    queryFn:  () => api.get('/jobs/parts-alerts').then(r => r.data),
  })

export const useJob = (id) =>
  useQuery({
    queryKey: ['job', id],
    queryFn:  () => api.get(`/jobs/${id}`).then(r => r.data),
    enabled:  !!id,
  })

export const useAddJob = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: data => api.post('/jobs', data).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  })
}

export const useToggleInvestigation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (jobId) => api.patch(`/jobs/${jobId}/investigate`).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['jobs'] })
      qc.invalidateQueries({ queryKey: ['job', data.id] })
    },
  })
}

export const useUpdateJob = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }) => api.patch(`/jobs/${id}`, data).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['jobs'] })
      qc.invalidateQueries({ queryKey: ['job', data.id] })
    },
  })
}

export const useDeleteJob = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: id => api.delete(`/jobs/${id}`).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['jobs'] }),
  })
}

export const useAddJobTask = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ jobId, ...data }) => api.post(`/jobs/${jobId}/tasks`, data).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['job', vars.jobId] }),
  })
}

export const useUpdateJobTask = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ jobId, taskId, ...data }) =>
      api.patch(`/jobs/${jobId}/tasks/${taskId}`, data).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['job', vars.jobId] }),
  })
}

export const useDeleteJobTask = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ jobId, taskId }) =>
      api.delete(`/jobs/${jobId}/tasks/${taskId}`).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['job', vars.jobId] }),
  })
}

export const useAddJobPart = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ jobId, ...data }) => api.post(`/jobs/${jobId}/parts`, data).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['job', vars.jobId] }),
  })
}

export const useUpdateJobPart = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ jobId, partId, ...data }) =>
      api.patch(`/jobs/${jobId}/parts/${partId}`, data).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['job', vars.jobId] }),
  })
}

export const useDeleteJobPart = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ jobId, partId }) =>
      api.delete(`/jobs/${jobId}/parts/${partId}`).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['job', vars.jobId] }),
  })
}

// ─── Parts ────────────────────────────────────────────────────────────────────

export const useParts = ({ garageId, vehicleId } = {}) =>
  useQuery({
    queryKey: ['parts', garageId ?? null, vehicleId ?? null],
    queryFn:  () => api.get('/parts', {
      params: {
        ...(garageId  ? { garageId }  : {}),
        ...(vehicleId ? { vehicleId } : {}),
      },
    }).then(r => r.data),
  })

export const useAddPart = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: data => api.post('/parts', data).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['parts'] }),
  })
}

export const useUpdatePart = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }) => api.patch(`/parts/${id}`, data).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['parts'] }),
  })
}

export const useDeletePart = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: id => api.delete(`/parts/${id}`).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['parts'] }),
  })
}

// ─── Quotes ───────────────────────────────────────────────────────────────────

export const useQuotes = (filters = {}) =>
  useQuery({
    queryKey: ['quotes', filters],
    queryFn:  () => api.get('/quotes', { params: filters }).then(r => r.data),
  })

export const useQuote = (id) =>
  useQuery({
    queryKey: ['quote', id],
    queryFn:  () => api.get(`/quotes/${id}`).then(r => r.data),
    enabled:  !!id,
  })

export const useAddQuote = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: data => api.post('/quotes', data).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['quotes'] }),
  })
}

export const useUpdateQuote = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }) => api.patch(`/quotes/${id}`, data).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['quotes'] })
      qc.invalidateQueries({ queryKey: ['quote', data.id] })
    },
  })
}

export const useAddQuoteItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ quoteId, ...data }) =>
      api.post(`/quotes/${quoteId}/items`, data).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['quote', vars.quoteId] }),
  })
}

export const useUpdateQuoteItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ quoteId, itemId, ...data }) =>
      api.patch(`/quotes/${quoteId}/items/${itemId}`, data).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['quote', vars.quoteId] }),
  })
}

export const useDeleteQuoteItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ quoteId, itemId }) =>
      api.delete(`/quotes/${quoteId}/items/${itemId}`).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['quote', vars.quoteId] }),
  })
}

export const useDeleteQuote = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.delete(`/quotes/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quotes'] }),
  })
}

export const useSendQuote = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.post(`/quotes/${id}/send`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quotes'] })
      qc.invalidateQueries({ queryKey: ['jobs'] })
    },
  })
}

export const useApproveQuote = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.post(`/quotes/${id}/approve`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quotes'] })
      qc.invalidateQueries({ queryKey: ['jobs'] })
    },
  })
}

export const useRejectQuote = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }) =>
      api.post(`/quotes/${id}/reject`, { reason }).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quotes'] }),
  })
}

// ─── Invoices ─────────────────────────────────────────────────────────────────

export const useInvoices = (filters = {}) =>
  useQuery({
    queryKey: ['invoices', filters],
    queryFn:  () => api.get('/invoices', { params: filters }).then(r => r.data),
  })

export const useInvoice = (id) =>
  useQuery({
    queryKey: ['invoice', id],
    queryFn:  () => api.get(`/invoices/${id}`).then(r => r.data),
    enabled:  !!id,
  })

export const useAddInvoice = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: data => api.post('/invoices', data).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['invoices'] }),
  })
}

export const useUpdateInvoice = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }) => api.patch(`/invoices/${id}`, data).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['invoice', data.id] })
    },
  })
}

export const useAddInvoiceItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, ...data }) =>
      api.post(`/invoices/${invoiceId}/items`, data).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['invoice', vars.invoiceId] }),
  })
}

export const useUpdateInvoiceItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, itemId, ...data }) =>
      api.patch(`/invoices/${invoiceId}/items/${itemId}`, data).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['invoice', vars.invoiceId] }),
  })
}

export const useDeleteInvoiceItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, itemId }) =>
      api.delete(`/invoices/${invoiceId}/items/${itemId}`).then(r => r.data),
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['invoice', vars.invoiceId] }),
  })
}

export const useSendInvoice = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.post(`/invoices/${id}/send`).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['invoices'] }),
  })
}

export const useFinaliseInvoice = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => api.post(`/invoices/${id}/finalise`).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['invoice', data.id] })
    },
  })
}

export const useMarkInvoicePaid = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, paymentMethod }) =>
      api.post(`/invoices/${id}/mark-paid`, { paymentMethod }).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['invoice', data.id] })
    },
  })
}

export const usePopulateInvoiceFromJob = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (invoiceId) => api.post(`/invoices/${invoiceId}/populate-from-job`).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['invoice', data.id] })
    },
  })
}

export const useAddPartReceipt = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ jobPartId, jobId, file, notes }) => {
      const fd = new FormData()
      fd.append('document', file)
      fd.append('jobPartId', jobPartId)
      fd.append('documentType', 'service_receipt')
      if (notes) fd.append('notes', notes)
      return api.post('/documents', fd).then(r => r.data)
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['documents', 'part', vars.jobPartId] })
      if (vars.jobId) qc.invalidateQueries({ queryKey: ['job', vars.jobId] })
    },
  })
}

export const usePartReceipts = (jobPartId) =>
  useQuery({
    queryKey: ['documents', 'part', jobPartId],
    queryFn:  () => api.get('/documents', { params: { jobPartId } }).then(r => r.data),
    enabled:  !!jobPartId,
  })

export const useAddInvoiceReceipt = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, file, notes }) => {
      const fd = new FormData()
      fd.append('document', file)
      fd.append('invoiceId', invoiceId)
      fd.append('documentType', 'service_receipt')
      if (notes) fd.append('notes', notes)
      return api.post('/documents', fd).then(r => r.data)
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['invoice', vars.invoiceId] })
    },
  })
}

// ─── Admin ────────────────────────────────────────────────────────────────────

export const useAdminUsers = (filters = {}) =>
  useQuery({
    queryKey: ['adminUsers', filters],
    queryFn:  () => api.get('/admin/users', { params: filters }).then(r => r.data),
  })

export const useAdminUpdateUser = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }) => api.patch(`/admin/users/${id}`, data).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['adminUsers'] }),
  })
}

export const useAdminCreateUser = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data) => api.post('/admin/users', data).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['adminUsers'] }),
  })
}

export const useAdminCustomerLookup = (email) =>
  useQuery({
    queryKey: ['adminCustomerLookup', email ?? ''],
    queryFn:  () => api.get('/admin/customers/lookup', { params: { email } }).then(r => r.data),
    enabled:  !!email && email.includes('@'),
    staleTime: 10_000,
  })

export const useAdminGarages = (filters = {}) =>
  useQuery({
    queryKey: ['adminGarages', filters],
    queryFn:  () => api.get('/admin/garages', { params: filters }).then(r => r.data),
  })

export const useAdminCreateGarage = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: data => api.post('/admin/garages', data).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['adminGarages'] }),
  })
}

export const useAuditLogs = (filters = {}) =>
  useQuery({
    queryKey: ['auditLogs', filters],
    queryFn:  () => api.get('/admin/audit-logs', { params: filters }).then(r => r.data),
  })

export const useFeatureFlags = (userId) =>
  useQuery({
    queryKey: ['featureFlags', userId ?? null],
    queryFn:  () => api.get('/admin/feature-flags', { params: userId ? { userId } : {} }).then(r => r.data),
  })

export const useSetFeatureFlag = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: data => api.post('/admin/feature-flags', data).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['featureFlags'] }),
  })
}

export const useDeleteFeatureFlag = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: id => api.delete(`/admin/feature-flags/${id}`).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['featureFlags'] }),
  })
}

// ─── Reports ─────────────────────────────────────────────────────────────────

export const useComplianceReport = () =>
  useQuery({
    queryKey: ['reports', 'compliance'],
    queryFn:  () => api.get('/reports/compliance').then(r => r.data),
  })

export const useCostsReport = () =>
  useQuery({
    queryKey: ['reports', 'costs'],
    queryFn:  () => api.get('/reports/costs').then(r => r.data),
  })

// ─── Push notifications ───────────────────────────────────────────────────────

export const useSubscribePush = () =>
  useMutation({
    mutationFn: ({ endpoint, p256dh, auth }) =>
      api.post('/push/subscribe', { endpoint, p256dh, auth }).then(r => r.data),
  })

export const useUnsubscribePush = () =>
  useMutation({
    mutationFn: (endpoint) =>
      api.delete('/push/unsubscribe', { data: { endpoint } }).then(r => r.data),
  })

// ─── Garages ──────────────────────────────────────────────────────────────────

export const useGarages = (search = '') =>
  useQuery({
    queryKey: ['garages', search],
    queryFn:  () => api.get('/garages', { params: search ? { search } : {} }).then(r => r.data),
    enabled:  true,
  })

// ─── Garage Requests ──────────────────────────────────────────────────────────

export const useGarageRequests = () =>
  useQuery({
    queryKey: ['garageRequests'],
    queryFn:  () => api.get('/garage-requests').then(r => r.data),
  })

export const useSubmitGarageRequest = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: data => api.post('/garage-requests', data).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['garageRequests'] }),
  })
}

export const useRespondToGarageRequest = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }) =>
      api.patch(`/garage-requests/${id}`, { status }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['garageRequests'] })
      qc.invalidateQueries({ queryKey: ['customers'] })
      qc.invalidateQueries({ queryKey: ['vehicles'] })
    },
  })
}

// ─── Email log ────────────────────────────────────────────────────────────────

export const useEmailLog = (filters = {}) =>
  useQuery({
    queryKey: ['emailLog', filters],
    queryFn:  () => api.get('/emails', { params: filters }).then(r => r.data),
    placeholderData: (prev) => prev,
  })

export const useEmailHtml = (id) =>
  useQuery({
    queryKey: ['emailHtml', id],
    queryFn:  () => api.get(`/emails/${id}/html`).then(r => r.data.html),
    enabled:  !!id,
    staleTime: Infinity,
  })

export const useAdminTestEmail = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (toEmail) => api.post('/admin/test-email', { toEmail }).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['emailLog'] }),
  })
}

// ─── Check-ins ────────────────────────────────────────────────────────────────

export const useCheckinTemplates = () =>
  useQuery({
    queryKey: ['checkinTemplates'],
    queryFn:  () => api.get('/checkins/templates').then(r => r.data),
  })

export const useCheckinTemplateItems = (templateId) =>
  useQuery({
    queryKey: ['checkinTemplateItems', templateId],
    queryFn:  () => api.get(`/checkins/templates/${templateId}/items`).then(r => r.data),
    enabled:  !!templateId,
  })

export const useCreateCheckinTemplate = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: data => api.post('/checkins/templates', data).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['checkinTemplates'] }),
  })
}

export const useUpdateCheckinTemplate = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }) => api.patch(`/checkins/templates/${id}`, data).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['checkinTemplates'] }),
  })
}

export const useDeleteCheckinTemplate = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: id => api.delete(`/checkins/templates/${id}`).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['checkinTemplates'] }),
  })
}

export const useDuplicateCheckinTemplate = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: id => api.post(`/checkins/templates/${id}/duplicate`).then(r => r.data),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['checkinTemplates'] }),
  })
}

export const useCreateCheckinTemplateItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ templateId, ...data }) =>
      api.post(`/checkins/templates/${templateId}/items`, data).then(r => r.data),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: ['checkinTemplateItems', vars.templateId] }),
  })
}

export const useUpdateCheckinTemplateItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, templateId, ...data }) =>
      api.patch(`/checkins/template-items/${id}`, data).then(r => r.data),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: ['checkinTemplateItems', vars.templateId] }),
  })
}

export const useDeleteCheckinTemplateItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, templateId }) =>
      api.delete(`/checkins/template-items/${id}`).then(r => r.data),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: ['checkinTemplateItems', vars.templateId] }),
  })
}

export const useVehicleCheckinSchedules = (vehicleId) =>
  useQuery({
    queryKey: ['checkinSchedules', vehicleId],
    queryFn:  () => api.get(`/checkins/vehicle/${vehicleId}/schedules`).then(r => r.data),
    enabled:  !!vehicleId,
  })

export const useCreateCheckinSchedule = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ vehicleId, ...data }) =>
      api.post(`/checkins/vehicle/${vehicleId}/schedules`, data).then(r => r.data),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: ['checkinSchedules', vars.vehicleId] }),
  })
}

export const useUpdateCheckinSchedule = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }) =>
      api.patch(`/checkins/schedules/${id}`, data).then(r => r.data),
    onSuccess: (data, vars) => {
      qc.invalidateQueries({ queryKey: ['checkinSchedules'] })
    },
  })
}

export const useDeleteCheckinSchedule = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, vehicleId }) =>
      api.delete(`/checkins/schedules/${id}`).then(r => r.data),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: ['checkinSchedules', vars.vehicleId] }),
  })
}

export const useVehicleCheckins = (vehicleId) =>
  useQuery({
    queryKey: ['checkins', vehicleId],
    queryFn:  () => api.get(`/checkins/vehicle/${vehicleId}/submissions`).then(r => r.data),
    enabled:  !!vehicleId,
  })

export const useCheckin = (id) =>
  useQuery({
    queryKey: ['checkin', id],
    queryFn:  () => api.get(`/checkins/submissions/${id}`).then(r => r.data),
    enabled:  !!id,
  })

export const useUpdateCheckin = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }) =>
      api.patch(`/checkins/submissions/${id}`, data).then(r => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['checkins'] })
      qc.invalidateQueries({ queryKey: ['checkin', data.id] })
    },
  })
}

// Public — no auth token needed (uses axios directly with the same base URL)
import axios from 'axios'
const publicApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL
    ? `${import.meta.env.VITE_API_URL}/api`
    : '/api',
  timeout: 15000,
})

export const useCheckinForm = (token) =>
  useQuery({
    queryKey: ['checkinForm', token],
    queryFn:  () => publicApi.get(`/checkins/form/${token}`).then(r => r.data),
    enabled:  !!token,
    retry:    false,
  })

export const useSubmitCheckin = (token) => {
  return useMutation({
    mutationFn: data => publicApi.post(`/checkins/form/${token}`, data).then(r => r.data),
  })
}
